import { IconAlertTriangle } from 'featherico';
import type * as React from 'react';
import { useEffect, useState } from 'react';
import Crossfade from '../../components/Crossfade';
import { useHeightAsCssVariable } from '../../hooks/useHeightAsCssVariable';
import { isFeatureEnabled } from '../../services/Features';
import styles from './style.m.less';

export const OFFLINE_WARNING_ENTRYPOINT_ID = 'offline-warning';

type Props = {
    mountNode: HTMLElement;
};

const OfflineWarning: React.FC<Props> = ({ mountNode }) => {
    const [isOnline, setIsOnline] = useState(
        window.navigator && typeof window.navigator.onLine === 'boolean' ? window.navigator.onLine : true
    );

    useEffect(() => {
        mountNode.classList.add(styles.container);
        return () => mountNode.classList.remove(styles.container);
    }, [mountNode]);

    useEffect(() => {
        const offlineHandler = () => setIsOnline(false);
        const onlineHandler = () => setIsOnline(true);
        window.addEventListener('offline', offlineHandler);
        window.addEventListener('online', onlineHandler);

        return () => {
            window.removeEventListener('offline', offlineHandler);
            window.removeEventListener('online', onlineHandler);
        };
    }, []);

    useHeightAsCssVariable('--offlineWarningOffset', mountNode);

    const alwaysOnline = isFeatureEnabled('FEATURE_alwaysOnline');

    return (
        <Crossfade>
            {isOnline || alwaysOnline ? (
                <noscript key="online" />
            ) : (
                <div className={styles.warning} key="offline">
                    <span>
                        <strong>
                            <IconAlertTriangle /> You seem to be offline.
                        </strong>{' '}
                        Changes made now will not be saved.
                    </span>
                </div>
            )}
        </Crossfade>
    );
};

export default OfflineWarning;
